<template>
	<div :class="classes" @click="close($event, true)">
		<div class="modal-wrap">
			<div
				:class="[
					'modal-inner',
					{
						'modal-inner--no-header': !hasHeader,
						'modal-inner--has-title': !!hasHeader && !!title,
						'has-close': showClose,
						transparent: transparent,
					},
				]"
				@click="stopClose">
				<header v-if="hasHeader" :class="{ 'modal-header': true, [headerAlignmentClass]: true, 'shuffle-up': shuffleUp }">
					<h2 v-if="title" class="modal-title">{{ title }}</h2>
					<button
						v-if="showClose"
						type="button"
						:class="{ 'modal-close': true, small: size === 'small', 'no-close-fade': noCloseFade }"
						aria-label="Close"
						@click="close" />
				</header>

				<div :class="['header', { 'no-margin-bottom': headerNoMarginBottom }]" v-if="$slots.header"><slot name="header" /></div>

				<div
					:class="['modal-inner-scroll', { 'has-header': !!$slots.header || (hasHeader && !!title), 'no-scroll': noScroll, 'no-header-padding': headerNoPadding }]"
					v-if="$slots.content">
					<slot name="content" />
				</div>

				<div class="footer-inner" v-if="$slots['footer-inner']"><slot name="footer-inner" /></div>

				<Loading position="inner" v-if="loading" :noBackground="true" :text="loadingText" />
			</div>
		</div>
	</div>
</template>

<script>
import { Teleport } from "vue";
import LoadingSpinner from "./../LoadingSpinner.vue";
export default {
	name: "Pupup",
	emits: ["close"],
	components: { Loading: LoadingSpinner, Teleport },
	props: {
		title: {
			type: String,
			default: "",
		},
		showClose: {
			type: Boolean,
			default: true,
		},
		size: {
			type: String,
			default: "small",
		},
		width: {
			type: Number,
		},
		full: {
			type: Boolean,
			default: false,
		},
		noScroll: {
			type: Boolean,
			default: false,
		},
		slider: {
			type: Boolean,
			default: false,
		},
		hasBaseButtons: {
			type: Boolean,
			default: true,
		},
		whiteOut: {
			type: Boolean,
			default: false,
		},
		scrollTop: {
			type: Number,
			default: 0,
		},
		scrollOnClose: {
			type: Boolean,
			default: true,
		},
		loading: {
			type: [String, Boolean],
			default: "",
		},
		hideOuter: {
			type: Boolean,
			default: false,
		},
		clickOverlayToClose: {
			type: Boolean,
			default: false,
		},
		shuffleUp: {
			type: Boolean,
			default: false,
		},
		headerNoMarginBottom: {
			type: Boolean,
			default: false,
		},
		headerNoPadding: {
			type: Boolean,
			default: false,
		},
		noCloseFade: {
			type: Boolean,
			default: false,
		},
		transparent: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			top: 0,
			closed: false,
		};
	},
	watch: {
		scrollTop() {
			const scrollableDivs = document.getElementsByClassName("modal-inner-scroll");
			Array.from(scrollableDivs).forEach(function (div) {
				div.scrollTop = 0;
			});
		},
	},
	computed: {
		loadingText() {
			if (!this.loading) return "";

			return typeof this.loading === "string" ? this.loading : "One moment...";
		},

		hasHeader() {
			return this.showClose || !!this.title;
		},

		headerAlignmentClass() {
			if (this.title && this.showClose) {
				return "modal-header--with-title";
			}
			if (this.title) {
				return "modal-header--title-only";
			}
			return "modal-header--close-only";
		},

		classes() {
			let classes = {
				modal: true,
				[this.size]: true,
				full: this.full,
				"no-base-buttons": !this.hasBaseButtons,
				"hide-outer": this.hideOuter,
			};

			if (this.width) {
				classes["width-" + this.width] = true;
				classes[this.size] = false;
			}

			if (this.noScroll) {
				classes["no-scroll"] = true;
			}

			classes["no-pupup-title"] = !this.title;

			if (this.slider) {
				classes["no-overlay2"] = true;
				classes["slider"] = true;
			}

			classes["white-out"] = !!this.loadingText;

			return classes;
		},
	},
	methods: {
		escHit(event) {
			if (event.key === "Escape") {
				this.close(event);
			}
		},

		clicked() {},
		closeBits() {
			document.body.classList.remove("modal-open");

			if (this.scrollOnClose) {
				setTimeout(
					function () {
						window.scrollTo(0, this.top);
					}.bind(this),
					1
				);
			}
		},
		close(event, isOverlay = false) {
			if (isOverlay && !this.clickOverlayToClose) return;

			this.closed = true;

			event.stopPropagation();

			this.closeBits();

			this.$emit("close");
		},
		stopClose(event) {
			event.stopPropagation();
		},
	},
	created() {
		this.top = (window.pageYOffset || document.documentElement.scrollTop) - (document.documentElement.clientTop || 0);

		document.body.classList.add("modal-open");

		document.addEventListener("keydown", this.escHit);
	},
	beforeUnmount() {
		if (!this.closed) {
			this.closeBits();
		}
		document.removeEventListener("keydown", this.escHit);
	},
};
</script>
<style lang="less" scoped>
@import "./../../assets/css/less/_settings.less";
@import "./../../assets/css/less/_helper.less";
.modal {
	&:not(.no-overlay2) {
		background-color: @overlayScrimSoft;
		position: fixed;
		z-index: 999;
		left: 0;
		top: 0;
		width: 100%;
		min-height: 100%;
	}
	.modal-wrap {
		padding: 20px 20px 0;
		.modal-inner {
			position: relative;
			margin: 0 auto;
			background-color: white;
			.elevation.modalWorkspace();
			overflow: hidden;
			border-radius: @radiusXl;
			display: flex;
			flex-direction: column;
			padding: 15px 0 0;
			max-height: calc(100vh - 40px);
			max-height: calc(100svh - 40px);
			max-height: calc(100dvh - 40px);
			max-width: 800px;

			&.modal-inner--no-header {
				padding-top: 24px;
			}

			&.modal-inner--has-title {
				padding-top: 0;
			}

			.header,
			.footer-inner,
			.modal-header {
				flex: 0 0 auto;
			}

			.modal-inner-scroll {
				position: relative;
				margin: 0;
				box-sizing: border-box;
				flex-grow: 1;
				min-height: 20px;
				max-height: 100%;
				z-index: 1;
				position: relative;
				padding: 0 24px 24px;
				overflow-x: hidden;
				overflow-y: scroll;

				&.has-header {
					padding-top: 16px;
					&.no-header-padding {
						padding-top: 0;
					}
				}
				&.no-scroll {
					overflow-y: hidden;
					max-height: 60vh;
				}
				:deep(p) {
					&:first-child {
						margin-top: 0;
					}
					&:last-child {
						margin-bottom: 0;
					}
				}
			}

			.modal-header {
				display: flex;
				align-items: center;
				gap: 12px;
				padding: 0 12px 0;
				margin-bottom: 0;

				&.modal-header--with-title {
					justify-content: space-between;
					padding-top: 0;
				}

				&.modal-header--title-only {
					justify-content: flex-start;
				}

				&.modal-header--close-only {
					justify-content: flex-end;
					margin-bottom: 0;
				}
				&.shuffle-up {
					position: absolute;
					width: 100%;
				}
			}

			.modal-title {
				margin: 0;
				font-size: 20px;
				font-weight: @bold;
				line-height: 1.3;
				color: @textGrey;
				padding: 20px 0 18px 12px;
				z-index: 2;
				width: 100%;
				box-sizing: border-box;
				text-align: left;
				position: relative;
				margin-bottom: 6px;
				letter-spacing: 0.005em;
				border-bottom: 1px solid @borderRoseSubtle;
				.link {
					color: inherit;
					text-decoration: none;
					&:hover {
						text-decoration: underline;
					}
				}
				span {
					color: inherit;
				}
			}

			.modal-close {
				display: inline-block;
				width: 3em;
				aspect-ratio: 1/1;
				height: auto;
				border-radius: @radiusCircle;
				border: none;
				background: white url("../../assets/img/close.svg") center center no-repeat;
				background-size: 45% auto;
				cursor: pointer;
				transition:
					background-color 0.2s ease,
					transform 0.5s ease-in-out;
				position: relative;
				z-index: 2;

				&:hover,
				&:focus-visible {
					background-color: @controlHoverDarkTint;
					color: @textStrong;
					transform: rotate(180deg);
				}

				&.no-close-fade {
					&:hover,
					&:focus-visible {
						background-color: white;
					}
				}

				&:focus {
					outline: none;
				}

				&:focus-visible {
					box-shadow: 0 0 0 2px fade(@blue, 20%);
				}
			}

			.header {
				padding: 0 24px;
				margin-bottom: 16px;
				position: relative;
				z-index: 2;

				&.no-margin-bottom {
					margin-bottom: 0;
				}
			}

			&.has-close {
				.header {
					margin-right: 60px;
					padding-right: 0;
				}
			}

			.footer-inner {
				padding: 16px 24px;
				border-top: 1px solid @borderRoseSubtle;
				:deep(.modal-actions) {
					display: flex;
					justify-content: flex-end;
					gap: 12px;
					&.center,
					&.centre {
						justify-content: center;
					}
					.button {
						margin-top: 0;
					}
				}
			}
			.iframe-wrap {
				position: relative;
				overflow: hidden;
				padding-top: 56.25%;
				margin-top: 15px;

				&:before {
					content: "Loading video..";
					text-align: center;
					font-size: 14px;
					color: @formDefaultColour;
					position: absolute;
					top: 20px;
					width: 100px;
					margin-left: -50px;
					left: 50%;
				}

				iframe {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					border: 0;
				}
			}

			&.transparent {
				background-color: transparent;
				box-shadow: none;
				.modal-close {
					top: -12px;
					right: -12px;
				}
				.modal-inner-scroll {
					padding: 0 8px;
				}
			}
		}
	}

	&.hide-outer {
		> .modal-wrap {
			> .modal-inner {
				background: none;
				box-shadow: none;

				> .modal-header {
					display: none;
				}
			}
		}
	}

	&.white-out {
		.modal-wrap {
			.modal-inner {
				background-color: #fff;
				> .header,
				> .footer-inner,
				> .modal-header,
				> .modal-inner-scroll {
					opacity: 0 !important;
				}
			}
		}
	}

	&.slider {
		max-width: 100vw;

		.modal-inner {
			padding-top: 15px;
		}
	}

	&.no-scroll {
		.modal-inner {
			overflow: visible;
			max-height: none;

			.modal-inner-scroll {
				overflow: visible;
				max-height: none;
			}
		}
	}

	@widths: 400, 500, 600, 700, 800, 900, 1000, 1200, 1300, 1400, 1500, 1600;
	.generate-widths(@i) when (@i > 0) {
		@w: extract(@widths, @i);
		&.width-@{w} {
			.modal-inner,
			.modal-inner-scroll {
				max-width: unit(@w, px);
			}
		}
		.generate-widths(@i - 1);
	}
	.generate-widths(length(@widths));

	.modal-inner-scroll {
		max-height: calc(100vh - 100px);
		max-height: calc(100svh - 100px);
		max-height: calc(100dvh - 100px);
		padding: 0 24px 24px;
	}

	&.full {
		&:not(.has-title) {
			.modal-inner-scroll {
				padding-top: 0px;
			}
		}

		&.no-base-buttons {
			.modal-inner-scroll {
				padding-top: 0;
				max-height: calc(100% - 20px);
			}
		}
		.modal-inner,
		.modal-inner-scroll {
			&:not(.ignore-full) {
				max-width: none;
				width: 100%;
			}
		}
		.modal-inner {
			&:not(.ignore-full) {
				height: calc(100vh - 40px);
				height: calc(100svh - 40px);
				height: calc(100dvh - 40px);
				max-height: none;
			}
		}
	}

	&.small {
		.modal-inner,
		.modal-inner-scroll {
			max-width: 400px;
		}

		.modal-inner {
			.modal-header {
				padding: 0px 20px 0;
				margin-bottom: 20px;
			}

			.modal-inner-scroll {
				padding: 0 20px 20px;
			}

			.header {
				padding: 0 20px;
			}

			.footer-inner {
				padding: 16px 20px;
			}
		}

		.modal-close {
			width: 32px;
			font-size: 18px;
		}
	}
}

@media screen and (max-width: 640px) {
	.modal {
		.modal-wrap {
			padding: 12px 12px 0;
			.modal-inner {
				.modal-inner-scroll {
					padding: 0 16px 24px;
				}
				.modal-title {
					padding-left: 2px;
				}
				&.transparent {
					.modal-inner-scroll {
						padding: 0;
					}
				}
			}
		}
	}
}

@media screen and (max-width: 439px) {
	.modal {
		&.slider {
			.modal-wrap .modal-inner .modal-inner-scroll {
				padding-bottom: 60px;
			}
		}
	}
}

@media screen and (min-width: 440px) {
	.modal {
		&.slider {
			max-width: 90vw;
			min-width: 80vw;
		}
	}
}

@media screen and (min-width: 1024px) {
	.modal {
		.modal-wrap {
			.modal-inner {
				&.transparent {
					.modal-inner-scroll {
						padding: 2px 12px 0;
					}
					.modal-close {
						right: -12px;
					}
				}
			}
		}
	}
}
</style>